// The 360 Overview's Notes panel (spec 09 §4.2's E2-2a1 annotation, D-323
// rulings 1 to 4; §5's E2-2 rows 1 to 3a).
//
// WHAT IT SHOWS. The customer's notes, the pinned note first and then the
// newest, a page at a time. A legacy note says "carried over"; a redacted note
// reads "Text removed by … on …". Every reader of the customer reads them, as
// the old `notes` field was read (spec 09 §7).
//
// WHO WRITES. A writer (§7 narrowing 22: sales on assigned customers,
// facility_admin, group_admin; finance holds the code for its credit cell
// alone and the server refuses it, so the mirror offers it nothing) has an add
// box of up to 4000 characters with a Pin checkbox, and Pin and Unpin on each
// note. An archived customer takes no new note and no pin (422), so it is
// offered neither. Redact shows on `perms.isAdmin` (§7 narrowing 18), behind a
// confirm that says the words cannot come back, and an archived customer's
// note can still be redacted, because a redaction erases words and adds no
// business.
//
// ONE KEY PER ATTEMPT (`lib/attempt-key.ts`). A failed Add keeps the draft and
// invites a second press, and notes are append-only with no way to remove a
// duplicate, so Add keeps one Idempotency-Key until the attempt is settled: a
// second press after a lost response is replayed from the server's store
// instead of writing the note twice. Editing the draft or the Pin box starts a
// new attempt, because the old key under a new body answers 409.
//
// STATES. Three skeleton rows while the first page loads; "No notes yet" when
// there is none, with the add box for a writer and the sentence alone for a
// reader; a failed read with its request id and Try again; a read held offline
// says so. A refused or failed write reads one line and KEEPS THE DRAFT, and
// while offline the draft stays and Add is disabled. On a phone the panel runs
// full width (sales.css).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { CheckboxField, Field } from "../../../../../components/field";
import { ErrorState, Skeleton } from "../../../../../components/states";
import { StatusChip } from "../../../../../components/status-chip";
import { RequestError, requestIdOf } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useAttemptKey } from "../../lib/attempt-key";
import { useReadsHeldOffline } from "../../lib/reads";
import {
  canRedactNotes,
  canWriteCustomerNotes,
  useSalesPermissions,
} from "../../hooks/permissions";
import {
  useAppendNote,
  useNoteVerb,
  type CustomerNote,
  type useCustomerNotes,
} from "../../hooks/crm-queries";
import { NoteBody, NoteMeta, isRedacted } from "./note-meta";
import { ListPager } from "../../../../../components/list-pager";
import { useLoadedPager } from "../../../../../lib/cursor-pages";
import { bookRange } from "../../hooks/book-pages";

/** A note's longest body, in UTF-16 units as sent (the contract's cap). */
export const NOTE_BODY_MAX = 4000;

/** Can this draft be sent? Something other than whitespace, within the cap. */
export function noteDraftSendable(draft: string): boolean {
  return draft.trim().length > 0 && draft.length <= NOTE_BODY_MAX;
}

/** The note doors' refusals the reader is told in words (D-225). */
const NOTE_REFUSALS = new Set(["CUSTOMER_ARCHIVED", "NOTE_NOT_FOUND", "CUSTOMER_NOT_FOUND"]);

export function noteRefusalKey(error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  if (error.code !== undefined && NOTE_REFUSALS.has(error.code)) {
    return `sales.notes.refusal.${error.code}`;
  }
  if (error.httpStatus === 403) return "sales.notes.refusal.forbidden";
  return undefined;
}

type NotesRead = ReturnType<typeof useCustomerNotes>;

export function NotesPanel({
  customerId,
  customerActive,
  notes,
}: {
  customerId: string;
  customerActive: boolean;
  /** The panel's read, started by the Overview so it runs beside the other
   * panels' reads rather than after the grid reveals. */
  notes: NotesRead;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const writer = canWriteCustomerNotes(perms);
  const redactor = canRedactNotes(perms);
  const offline = useReadsHeldOffline();
  const append = useAppendNote(customerId);
  const appendKey = useAttemptKey();
  const noteVerb = useNoteVerb(customerId);
  const [draft, setDraft] = useState("");
  const [pin, setPin] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);

  // One page at a time (D-560): Next reads the following page when it is not
  // loaded yet, and the feed never folds every page into one long column.
  const notePages = useLoadedPager(notes, customerId);
  const items: readonly CustomerNote[] = notePages.view.items;
  const canAdd = writer && customerActive;
  const appendRefusal = append.isError ? noteRefusalKey(append.error) : undefined;
  const verbRefusal = noteVerb.isError ? noteRefusalKey(noteVerb.error) : undefined;
  const busy = noteVerb.isPending;

  const body = (() => {
    if (notes.data === undefined) {
      if (notes.error !== null) {
        return (
          <ErrorState
            title={t("sales.notes.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(notes.error)}
            onRetry={() => {
              void notes.refetch();
            }}
          />
        );
      }
      if (notes.fetchStatus === "paused") {
        return (
          <p className="sales-hint" data-testid="sales-notes-offline">
            {t("sales.notes.readOffline")}
          </p>
        );
      }
      return (
        <div className="sales-notes-skeleton" data-testid="sales-notes-loading" aria-busy="true">
          <p className="visually-hidden" role="status">
            {t("sales.notes.loading")}
          </p>
          {[0, 1, 2].map((row) => (
            <div key={row} className="sales-note-row" aria-hidden="true">
              <Skeleton height="1rem" />
              <Skeleton width="40%" height="0.8rem" />
            </div>
          ))}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <p className="sales-hint" data-testid="sales-notes-empty">
          {t("sales.notes.empty")}
        </p>
      );
    }
    return (
      <>
        <ul className="sales-notes-list" data-testid="sales-notes-list">
          {items.map((note) => (
            <li
              key={note.id}
              className="sales-note-row"
              data-testid="sales-note-row"
              data-note-id={note.id}
              data-pinned={note.pinned ? "true" : "false"}
              data-redacted={isRedacted(note) ? "true" : "false"}
            >
              <div className="sales-note-head">
                {note.pinned ? (
                  <StatusChip variant="accent" dot={false} data-testid="sales-note-pinned">
                    {t("sales.notes.pinned")}
                  </StatusChip>
                ) : null}
                <NoteMeta note={note} />
              </div>
              <NoteBody note={note} />
              {confirming === note.id ? (
                <div
                  className="sales-note-confirm"
                  role="group"
                  data-testid="sales-note-redact-confirm"
                >
                  <p className="sales-note-confirm-text">{t("sales.notes.redactConfirm")}</p>
                  <Button
                    size="sm"
                    variant="danger"
                    data-testid="sales-note-redact-yes"
                    disabled={offline}
                    busy={busy}
                    busyLabel={t("sales.common.saving")}
                    onClick={() => {
                      noteVerb.mutate(
                        { verb: "redact", noteId: note.id },
                        {
                          onSettled: () => {
                            setConfirming(null);
                          },
                        },
                      );
                    }}
                  >
                    {t("sales.notes.redactYes")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setConfirming(null);
                    }}
                  >
                    {t("sales.common.cancel")}
                  </Button>
                </div>
              ) : (
                <div className="sales-note-actions">
                  {canAdd && !note.pinned ? (
                    <Button
                      size="sm"
                      data-testid="sales-note-pin"
                      disabled={busy || offline}
                      onClick={() => {
                        noteVerb.mutate({ verb: "pin", noteId: note.id });
                      }}
                    >
                      {t("sales.notes.pin")}
                    </Button>
                  ) : null}
                  {canAdd && note.pinned ? (
                    <Button
                      size="sm"
                      data-testid="sales-note-unpin"
                      disabled={busy || offline}
                      onClick={() => {
                        noteVerb.mutate({ verb: "unpin", noteId: note.id });
                      }}
                    >
                      {t("sales.notes.unpin")}
                    </Button>
                  ) : null}
                  {redactor && !isRedacted(note) ? (
                    <Button
                      size="sm"
                      variant="danger"
                      data-testid="sales-note-redact"
                      disabled={busy || offline}
                      onClick={() => {
                        noteVerb.reset();
                        setConfirming(note.id);
                      }}
                    >
                      {t("sales.notes.redact")}
                    </Button>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
        <ListPager
          range={bookRange(t, i18n.language, notePages.view)}
          hasPrev={notePages.view.hasPrev}
          hasNext={notePages.view.hasNext && !offline}
          busy={notePages.turning}
          busyLabel={t("sales.common.loading")}
          prevLabel={t("sales.common.pagePrev")}
          nextLabel={t("sales.common.pageNext")}
          onPrev={notePages.prev}
          onNext={notePages.next}
          testId="sales-notes-pager"
        />
        {notes.isFetchNextPageError ? (
          <p className="sales-error-note" role="alert" data-testid="sales-notes-more-failed">
            {t("sales.notes.moreFailed")}
          </p>
        ) : null}
      </>
    );
  })();

  return (
    <div
      className="ds-card sales-360-card sales-360-wide sales-notes"
      data-testid="sales-360-notes"
    >
      <h3>{t("sales.notes.title")}</h3>

      {canAdd ? (
        <div className="sales-note-add" data-testid="sales-note-add">
          <Field label={t("sales.notes.addLabel")} wide className="sales-note-field">
            <textarea
              value={draft}
              rows={3}
              maxLength={NOTE_BODY_MAX}
              placeholder={t("sales.notes.addHint")}
              data-testid="sales-note-draft"
              onChange={(event) => {
                append.reset();
                appendKey.reset();
                // U+0000 cannot be stored (the contract refuses it 400), so
                // the box never holds one.
                setDraft(event.target.value.replaceAll("\u0000", ""));
              }}
            />
          </Field>
          <div className="sales-note-add-row">
            <span className="sales-hint num" data-testid="sales-note-count">
              {t("sales.notes.count", { count: draft.length, max: NOTE_BODY_MAX })}
            </span>
            <CheckboxField
              label={t("sales.notes.pinOnAdd")}
              checked={pin}
              data-testid="sales-note-pin-on-add"
              onChange={(checked) => {
                appendKey.reset();
                setPin(checked);
              }}
            />
            {/* Secondary: design-system's Button rule allows one primary per
                view, and the 360 is a view with many panels. */}
            <Button
              size="sm"
              data-testid="sales-note-submit"
              disabled={offline || !noteDraftSendable(draft)}
              busy={append.isPending}
              busyLabel={t("sales.notes.adding")}
              onClick={() => {
                append.mutate(
                  { body: draft, pin, key: appendKey.take() },
                  {
                    onSuccess: () => {
                      appendKey.reset();
                      setDraft("");
                      setPin(false);
                    },
                    onError: (error) => {
                      appendKey.failed(error);
                    },
                  },
                );
              }}
            >
              {t("sales.notes.add")}
            </Button>
          </div>
          {offline ? (
            <p className="sales-hint" data-testid="sales-note-offline">
              {t("sales.notes.offline")}
            </p>
          ) : null}
          {append.isError ? (
            <MutationFailureNote
              data-testid="sales-note-add-failed"
              text={appendRefusal !== undefined ? t(appendRefusal) : t("sales.notes.writeFailed")}
              error={append.error}
            />
          ) : null}
        </div>
      ) : writer && !customerActive ? (
        <p className="sales-hint" data-testid="sales-notes-archived">
          {t("sales.notes.archivedReadOnly")}
        </p>
      ) : null}

      {noteVerb.isError ? (
        <MutationFailureNote
          data-testid="sales-note-verb-failed"
          text={verbRefusal !== undefined ? t(verbRefusal) : t("sales.notes.verbFailed")}
          error={noteVerb.error}
        />
      ) : null}

      {body}
    </div>
  );
}
